/**
 * Pending CIB / SATIM payment bookkeeping.
 *
 * On web a card payment sends the customer to the SATIM gateway via a full-page
 * redirect, so the SPA is torn down. We persist the in-flight transaction here
 * (localStorage survives the cross-origin round trip) so that when the customer
 * lands back on our origin we can verify the payment status — mirroring the
 * mobile app, which checks `/payment/transaction/{ulid}/status` on return.
 */

const KEY = "fast_pending_payment";
/** Stale pending payments are ignored after this — long enough to finish on the
 *  gateway, short enough not to hijack an unrelated later visit. */
const TTL_MS = 15 * 60 * 1000;

export interface PendingPayment {
  /** ULID returned by POST /payment/intent — used to poll the status. */
  transactionUlid?: string;
  /** What is being paid — drives where the customer goes on success. */
  type?: "order_payment" | "wallet_topup";
  /** The order being paid for — used to route to its detail on success. */
  orderUlid?: string;
  /** The wallet being topped up (type === "wallet_topup"). */
  walletUlid?: string;
  storeUlid?: string;
  storeName?: string;
  amount?: number;
  /** epoch ms, set on save. */
  createdAt: number;
}

export function savePendingPayment(p: Omit<PendingPayment, "createdAt">): void {
  if (typeof window === "undefined") return;
  try {
    const payload: PendingPayment = { ...p, createdAt: Date.now() };
    localStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    /* storage unavailable (private mode / quota) — non-fatal */
  }
}

export function readPendingPayment(): PendingPayment | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as PendingPayment;
    if (!p?.createdAt || Date.now() - p.createdAt > TTL_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return p;
  } catch {
    return null;
  }
}

export function clearPendingPayment(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* non-fatal */
  }
}

export type PaymentOutcome = "success" | "failed" | "pending";

/**
 * Normalise the many status strings a payment gateway / backend can return into
 * the three outcomes the UI cares about. The backend documents
 * `pending | completed | failed | cancelled`, but the mobile app also accepts
 * `success`, so we stay defensive here.
 */
export function classifyPaymentStatus(status: string | null | undefined): PaymentOutcome {
  const s = (status ?? "").toLowerCase().trim();
  if (["completed", "success", "succeeded", "paid", "captured", "approved"].includes(s)) {
    return "success";
  }
  if (
    ["failed", "cancelled", "canceled", "declined", "rejected", "expired", "error", "refused"].includes(s)
  ) {
    return "failed";
  }
  return "pending";
}
